<template>
    <div class="editor-field-frame">
        <!-- Dauerhaft sichtbares Label ///////////////////////////////////// -->
        <div v-if="label || requirementText" class="editor-field-frame__header">
            <label v-if="label" class="editor-field-frame__label" :for="controlId">{{ label }}</label>
            <span v-if="requirementText" class="editor-field-frame__requirement">{{ requirementText }}</span>
        </div>

        <!-- Dauerhaft sichtbarer Hilfetext ///////////////////////////////// -->
        <p v-if="showHint" class="editor-field-frame__hint" :id="hintId">{{ hint }}</p>

        <!-- Das eigentliche Control liefert die Feldkomponente ///////////// -->
        <div class="editor-field-frame__control">
            <slot :control-id="controlId" :hint-id="hintId" />
        </div>

        <!-- Fehler beziehungsweise Status /////////////////////////////////// -->
        <div v-if="statusText"
             class="editor-field-frame__status"
             :class="`editor-field-frame__status--${statusSeverity}`"
             :role="statusSeverity === 'error' ? 'alert' : null">
            <v-icon :icon="statusIcon" size="small" />
            <span>{{ statusText }}</span>
        </div>
    </div>
</template>

<script setup>
/**
 * Gemeinsame Feldhülle aller Kundeneditor-Felder.
 *
 * Label, Hilfetext, Pflichtstatus, Abstände und Fehlerposition werden hier
 * zentral dargestellt. Die einzelne Feldkomponente liefert nur noch das
 * Control. Damit sehen gleiche Feldtypen appübergreifend gleich aus.
 *
 * Bewusst NICHT Teil der Props: Vuetify-Varianten, Breiten oder Farben. Eine
 * App-Editor-Definition darf das Kundeneditor-Styling nicht bestimmen.
 *
 * @see resources/css/components/editor.scss
 */
import {computed} from "vue";
import {useI18n} from "@/i18n";

const {t} = useI18n();

const props = defineProps({
    label: {
        type: String,
        default: null,
    },
    hint: {
        type: String,
        default: null,
    },
    /**
     * Fehlermeldung(en). Ein Fehler gewinnt immer gegen eine Warnung
     * beziehungsweise einen Statustext.
     */
    error: {
        type: [String, Array],
        default: null,
    },
    warning: {
        type: [String, Array],
        default: null,
    },
    /** Neutrale Statusmeldung, zum Beispiel "Bild wird hochgeladen". */
    status: {
        type: String,
        default: null,
    },
    required: {
        type: Boolean,
        default: false,
    },
    /**
     * Sollen "optional"-/"erforderlich"-Hinweise angezeigt werden?
     * Standard ist aus - die meisten Editor-Definitionen sagen dazu nichts.
     */
    showRequirement: {
        type: Boolean,
        default: false,
    },
    /** Eindeutige Kennung für die Label-Control-Verknüpfung. */
    controlId: {
        type: String,
        default: null,
    },
});

const hintId = computed(() => props.controlId ? `${props.controlId}-hint` : null);

/**
 * Ein Hinweis, der nur das Label wiederholt, ist kein Hinweis.
 * Genau das kommt in bestehenden Configs häufig vor.
 */
const showHint = computed(() => {
    if ( ! props.hint) return false;

    return String(props.hint).trim() !== String(props.label ?? '').trim();
});

function firstOf(value) {
    if (Array.isArray(value)) return value.find(entry => !! entry) ?? null;

    return value || null;
}

const statusSeverity = computed(() => {
    if (firstOf(props.error)) return 'error';
    if (firstOf(props.warning)) return 'warning';

    return 'info';
});

const statusText = computed(() =>
    firstOf(props.error) ?? firstOf(props.warning) ?? props.status ?? null);

// Bedeutung nie nur über Farbe: jeder Status hat ein eigenes Icon.
const statusIcon = computed(() => ({
    error: 'mdi-alert-circle-outline',
    warning: 'mdi-alert-outline',
    info: 'mdi-information-outline',
}[statusSeverity.value]));

const requirementText = computed(() => {
    if ( ! props.showRequirement) return null;

    return props.required ? t('editor.fields.requirement.required') : t('editor.fields.requirement.optional');
});
</script>
